import React, { useState } from "react";
import { Input } from "../Input/Input";
import { Button } from "../Button/Button";
import { Alert } from "../Alert/Alert";
import { Card } from "../Card/Card";
import { Space } from "../Layout/Grid";
import { MailOutline as MailOutlined } from "@mui/icons-material";
import { useCognitoAuth } from "./CognitoAuthProvider";
import { UserAuthType } from "../../graphql-types/graphql";

interface IdpLookupResponse {
  data: {
    lookupIdpByEmail: {
      cognitoIdpIdentifier: string | null;
      found: boolean;
      authType: UserAuthType;
    };
  };
}

interface CognitoLoginPageProps {
  graphqlUri: string;
  title?: string;
  subtitle?: string;
  logoUrl?: string;
}

export const CognitoLoginPage: React.FC<CognitoLoginPageProps> = ({
  graphqlUri,
  title = "Sign In to Sirius",
  subtitle = "Enter your email to continue",
  logoUrl,
}) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const { signInWithIdP, signIn } = useCognitoAuth();

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }

    setLookupLoading(true);

    try {
      // Look up IdP by email using fetch (can't use Apollo - not authenticated yet)
      const response = await fetch(graphqlUri, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          query: `
            query LookupIdpByEmail($email: String!) {
              lookupIdpByEmail(email: $email) {
                cognitoIdpIdentifier
                found
                authType
              }
            }
          `,
          variables: { email: email.toLowerCase() },
        }),
      });

      const result = (await response.json()) as IdpLookupResponse;
      const { authType, cognitoIdpIdentifier } =
        result.data?.lookupIdpByEmail || {};

      switch (authType) {
        case UserAuthType.Saml:
          // SAML user - redirect to their IdP
          if (cognitoIdpIdentifier) {
            await signInWithIdP(cognitoIdpIdentifier);
          } else {
            setError(
              "SAML configuration error. Please contact your administrator."
            );
          }
          break;

        case UserAuthType.CustomAuth:
          // Custom auth user (ODC/business app) - redirect to custom auth flow
          window.location.href = "/login/external";
          break;

        case UserAuthType.NotFound:
        default:
          // User not found
          setError(
            "Email address not found. Please check your email or contact your administrator."
          );
          break;
      }
    } catch (err) {
      console.error("IdP lookup failed", err);
      setError("Failed to look up identity provider. Please try again.");
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        background: "#f0f2f5",
      }}
    >
      <Card
        style={{
          width: 450,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          borderRadius: 8,
        }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {logoUrl && (
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <img
                src={logoUrl}
                alt="Logo"
                style={{ maxWidth: "200px", height: "auto" }}
              />
            </div>
          )}

          <div style={{ textAlign: "center" }}>
            <h1
              style={{
                fontSize: 24,
                fontWeight: 500,
                marginBottom: 8,
                color: "#262626",
              }}
            >
              {title}
            </h1>
            <p style={{ color: "#8c8c8c", fontSize: 14, margin: 0 }}>
              {subtitle}
            </p>
          </div>

          <form onSubmit={handleEmailSubmit}>
            <Space direction="vertical" size="middle" style={{ width: "100%" }}>
              <Input
                size="large"
                placeholder="Email address"
                prefix={<MailOutlined style={{ color: "#bfbfbf" }} />}
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={lookupLoading}
              />

              {error && (
                <Alert
                  message={error}
                  type="error"
                  showIcon
                  closable
                  onClose={() => setError(null)}
                />
              )}

              <Button
                type="primary"
                size="large"
                htmlType="submit"
                loading={lookupLoading}
                block
              >
                {lookupLoading ? "Looking up..." : "Continue"}
              </Button>
            </Space>
          </form>
        </Space>
      </Card>
    </div>
  );
};
